import { useEffect, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PackshotJob } from '@/lib/packshots';
import { cn } from '@/lib/utils';

const LEAN_MENTION_MIN = 1.5;
const LEAN_MENTION_MAX = 15;
const STRAIGHTEN_LIMIT = 45;
const STEP = 0.5;

export function straightenOffer(job: PackshotJob | null): { lean: number; applied: number } | null {
  if (!job?.files.output || !job.files.cutout) return null;
  if (job.status !== 'needs_review' && job.status !== 'completed' && job.status !== 'failed') return null;
  const lean = job.placement?.leanDegrees;
  const applied = job.placement?.straightenDegrees ?? 0;
  if (typeof lean !== 'number' || !Number.isFinite(lean) || !Number.isFinite(applied)) return null;
  const flagged = Math.abs(lean) >= LEAN_MENTION_MIN && Math.abs(lean) <= LEAN_MENTION_MAX;
  if (!flagged && Math.abs(applied) < 0.05) return null;
  return { lean, applied };
}

function roundTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatSigned(degrees: number): string {
  const rounded = roundTenth(degrees);
  const text = Math.abs(rounded).toFixed(1);
  if (rounded > 0) return `+${text}°`;
  if (rounded < 0) return `-${text}°`;
  return '0.0°';
}

type Props = {
  lean: number;
  applied: number;
  canWrite: boolean;
  pending: boolean;
  onAngle: (degrees: number) => void;
  onApply: (degrees: number) => void;
  onInteract: () => void;
};

export default function StraightenControl({
  lean,
  applied,
  canWrite,
  pending,
  onAngle,
  onApply,
  onInteract,
}: Props) {
  const [angle, setAngle] = useState(applied);
  const suggestion = roundTenth(lean);
  const dirty = Math.abs(angle - applied) >= 0.05;
  const saved = Math.abs(applied) >= 0.05;
  const offset = roundTenth(lean - angle);
  const remaining = Math.abs(offset);
  const tilted = remaining >= LEAN_MENTION_MIN && remaining <= LEAN_MENTION_MAX;
  const degreesOff = Math.max(1, Math.round(remaining));
  const canStraighten = Math.abs(angle - suggestion) >= 0.05;

  useEffect(() => {
    setAngle(applied);
  }, [applied]);

  useEffect(() => {
    onAngle(angle);
  }, [angle, onAngle]);

  function choose(next: number) {
    const clamped = Math.min(STRAIGHTEN_LIMIT, Math.max(-STRAIGHTEN_LIMIT, roundTenth(next)));
    setAngle(clamped);
  }

  function nudge(delta: number) {
    choose(angle - delta);
    onInteract();
  }

  function straighten() {
    choose(suggestion);
    onInteract();
    onApply(suggestion);
  }

  return (
    <div className="space-y-3 rounded-2xl border border-border bg-card p-3">
      {tilted ? (
        <p className="text-sm leading-snug">
          Looks about <span className="font-medium tabular-nums">{degreesOff}°</span> off straight.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">Straight. Nudge if it still looks off.</p>
      )}
      {canWrite && (
        <>
          <div className="flex items-center gap-2">
            <div className="flex h-9 min-w-0 flex-1 items-center overflow-hidden rounded-full border border-border bg-background">
              <button
                type="button"
                className="flex h-full w-9 shrink-0 items-center justify-center text-foreground hover:bg-muted disabled:opacity-40"
                disabled={pending || angle >= STRAIGHTEN_LIMIT}
                aria-label="Decrease the offset half a degree"
                onClick={() => nudge(-STEP)}
              >
                <Minus className="size-3.5" aria-hidden="true" />
              </button>
              <span className={cn('min-w-0 flex-1 border-x border-border px-2 text-center text-sm tabular-nums')} aria-live="polite">
                {formatSigned(offset)}
              </span>
              <button
                type="button"
                className="flex h-full w-9 shrink-0 items-center justify-center text-foreground hover:bg-muted disabled:opacity-40"
                disabled={pending || angle <= -STRAIGHTEN_LIMIT}
                aria-label="Increase the offset half a degree"
                onClick={() => nudge(STEP)}
              >
                <Plus className="size-3.5" aria-hidden="true" />
              </button>
            </div>
            <Button type="button" className="h-9 shrink-0 px-3" disabled={pending || !canStraighten} onClick={straighten}>
              Auto
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="outline" disabled={pending || !dirty} onClick={() => { onInteract(); onApply(roundTenth(angle)); }}>
              Apply
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={pending || !saved}
              onClick={() => {
                choose(0);
                onInteract();
                onApply(0);
              }}
            >
              Reset
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
